import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Button,
  Checkbox,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Toolbar,
  ToolbarButton,
  Tooltip,
  Spinner,
} from "@fluentui/react-components";
import {
  Warning24Regular,
  ArrowLeft20Regular,
} from "@fluentui/react-icons";
import { useDevice } from "../../../model";
import { useAppStyles } from "../../styles/appStyles";
import { config } from "../../../config/environment";

interface DeleteCompanyPageProps {
  companyId: string;
  companyName: string;
  onBack: () => void;
  onSuccess?: () => void;
}

const DeleteCompanyPage: React.FC<DeleteCompanyPageProps> = ({
  companyId,
  companyName,
  onBack,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const styles = useAppStyles();
  const { fingerprint, refreshSubscription } = useDevice();

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleDeleteCompany = async () => {
    if (!isConfirmed) {
      setError(t("delete_company.error.confirm_required", "Musisz potwierdzić operację"));
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      console.log("🗑️ Starting delete company process...");

      const deleteUrl = `${config.api.baseUrl}/api/v1/Subscription/deletecompany`;
      console.log("📡 Calling delete company API:", deleteUrl);

      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          deviceFingerprint: fingerprint,
          companyId: companyId,
        }),
      });

      if (!response.ok) {
        let errorMessage = t("delete_company.error.unknown", "Nieznany błąd usuwania firmy");

        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            console.error("❌ API Error response:", errorData);

            if (errorData.details) {
              errorMessage = errorData.details;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else {
            const textError = await response.text();
            errorMessage = textError || `${t("delete_company.error.http", "Błąd HTTP")} ${response.status}`;
          }
        } catch (parseError) {
          console.error("❌ Error parsing error response:", parseError);
          errorMessage = `${t("delete_company.error.http", "Błąd HTTP")} ${response.status}`;
        }

        throw new Error(errorMessage);
      }

      console.log("✅ Company deleted successfully");

      // Refresh subscription to get updated data
      await refreshSubscription();

      setSuccess(true);

      // Po krótkiej chwili wróć lub wywołaj callback sukcesu
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          onBack();
        }
      }, 2000);
    } catch (err) {
      console.error("❌ Delete company error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles.appPageContainer}>
      <div
        className={styles.appPageHeader}
        style={{ background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)" }}
      >
        <Warning24Regular className={styles.appPageHeaderIcon} style={{ color: "#ffffff" }} />
        <Text className={styles.appPageTitle} style={{ color: "#ffffff" }}>
          {t("delete_company.title", "Usuwanie firmy z subskrypcji")}
        </Text>
      </div>

      <Toolbar className={styles.toolbar}>
        <Tooltip
          content={t("delete_company.toolbar.back_tooltip", "Powrót do poprzedniej strony")}
          relationship="description"
        >
          <ToolbarButton
            className={styles.toolbarButton}
            aria-label={t("delete_company.toolbar.back", "Powrót")}
            icon={<ArrowLeft20Regular className={styles.backIcon} />}
            onClick={onBack}
            disabled={isDeleting}
          >
            {t("delete_company.toolbar.back", "Powrót")}
          </ToolbarButton>
        </Tooltip>
      </Toolbar>

      <div className={styles.appPageContent}>
        {/* Success Message */}
        {success && (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>
                {t("delete_company.success.title", "Firma usunięta pomyślnie!")}
              </MessageBarTitle>
              {t("delete_company.success.message", "Firma została usunięta z subskrypcji. Przekierowywanie...")}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Error Message */}
        {error && !success && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {t("delete_company.error.title", "Błąd usuwania firmy")}
              </MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Warnings and Confirmations */}
        {!success && (
          <>
            {/* Company Name Display */}
            <div
              style={{
                marginBottom: "16px",
                padding: "10px 12px",
                background: "#f5f5f5",
                borderRadius: "4px",
                borderLeft: "4px solid #d32f2f",
              }}
            >
              <Text weight="semibold" size={300}>
                {t("delete_company.company_label", "Firma")}: {companyName}
              </Text>
            </div>

            {/* Important Warning */}
            <MessageBar intent="warning" style={{ marginBottom: "16px" }}>
              <MessageBarBody>
                <MessageBarTitle>{t("delete_company.warning.important", "Ważna informacja")}</MessageBarTitle>
                {t(
                  "delete_company.warning.description",
                  "Ta operacja usunie firmę wraz ze wszystkimi powiązanymi danymi z Twojej subskrypcji."
                )}
              </MessageBarBody>
            </MessageBar>

            {/* Critical Warning */}
            <MessageBar intent="error" style={{ marginBottom: "16px" }}>
              <MessageBarBody>
                <MessageBarTitle>
                  {t(
                    "delete_company.warning.critical",
                    "UWAGA: Po usunięciu firmy nie będzie możliwe jej ponowne dodanie przez 365 dni od dnia usunięcia."
                  )}
                </MessageBarTitle>
              </MessageBarBody>
            </MessageBar>

            {/* Confirmation Checkbox */}
            <div style={{ marginBottom: "24px", padding: "16px", background: "#f3f2f1", borderRadius: "8px" }}>
              <Checkbox
                checked={isConfirmed}
                onChange={(e, data) => setIsConfirmed(data.checked === true)}
                disabled={isDeleting}
                label={t(
                  "delete_company.confirmation.checkbox",
                  "Rozumiem konsekwencje i chcę trwale usunąć tę firmę z subskrypcji. Jestem świadomy/a, że nie będę mógł/mogła dodać jej ponownie przez 365 dni."
                )}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Button
                appearance="primary"
                onClick={handleDeleteCompany}
                disabled={isDeleting || !isConfirmed}
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                style={{
                  width: "100%",
                  background: isConfirmed && !isDeleting ? "#d32f2f" : undefined,
                  borderColor: isConfirmed && !isDeleting ? "#d32f2f" : undefined,
                }}
              >
                {isDeleting
                  ? t("delete_company.button.deleting", "Usuwanie...")
                  : t("delete_company.button.delete", "Usuń firmę")}
              </Button>
              <Button appearance="secondary" onClick={onBack} disabled={isDeleting} style={{ width: "100%" }}>
                {t("delete_company.button.cancel", "Anuluj")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteCompanyPage;
